/** Review links resolve into the normal workspace with a pinned branch overlay. */
export const DRAFT_REVIEW_ROUTE_PREFIX = '/app/build/review';
const EDITOR_ROUTES = {
  ontology_schema_delta: { path: '/app/ontology-builder', queryKey: null },
  view: { path: '/app/views', queryKey: 'view' },
  automation: { path: '/app/automations', queryKey: 'automation' },
  operational_process_version: { path: '/app/build', queryKey: 'process' },
};
export const editorRouteForFamily = (family) => EDITOR_ROUTES[family] || null;
export const isConfigureOverlayFamily = (family) => Boolean(EDITOR_ROUTES[family]);
export const isPreviewableFamily = (family) => family === 'view';

export const draftReviewPath = (
  sandboxId,
  { revisionId, overlayId, approvalId, artifactId, family, name } = {}
) => {
  const params = new URLSearchParams();
  if (revisionId || overlayId) params.set('revision', revisionId || overlayId);
  if (approvalId) params.set('approval', approvalId);
  if (artifactId) params.set('artifact', artifactId);
  if (family) params.set('family', family);
  if (name) params.set('name', name);
  return (
    (sandboxId ? DRAFT_REVIEW_ROUTE_PREFIX + '/' + encodeURIComponent(sandboxId) : '/app/build') +
    (params.size ? '?' + params : '')
  );
};
const overlayParams = ({ sandboxId, revisionId, overlayId, approvalId } = {}) => {
  const params = new URLSearchParams();
  if (sandboxId) params.set('sandbox', sandboxId);
  if (revisionId || overlayId) params.set('overlay', revisionId || overlayId);
  if (approvalId) params.set('approval', approvalId);
  return params;
};
export const branchChangesPath = (context, { canOpenEditors = true } = {}) => {
  const params = overlayParams(context);
  params.set('changes', '1');
  return (canOpenEditors ? '/app/build' : '/app/views') + '?' + params;
};
export const sandboxArtifactEditorPath = ({
  family,
  sandboxId,
  overlayId,
  revisionId,
  approvalId,
  name,
  artifactId,
  step,
} = {}) => {
  const route = EDITOR_ROUTES[family];
  if (!route) return branchChangesPath({ sandboxId, overlayId, revisionId, approvalId });
  const params = overlayParams({
    sandboxId,
    overlayId,
    revisionId,
    approvalId,
  });
  if (name && route.queryKey) params.set(route.queryKey, name);
  if (artifactId) params.set('artifact', artifactId);
  if (family === 'ontology_schema_delta') params.set('view', 'graph');
  if (family === 'operational_process_version') {
    if (step) params.set('step', step);
  }
  return route.path + (params.size ? '?' + params : '');
};
export const sandboxOntologyOverlayPath = (sandboxId, pin = {}) =>
  sandboxArtifactEditorPath({
    family: 'ontology_schema_delta',
    sandboxId,
    ...pin,
  });
export const sandboxProcessOverlayPath = (sandboxId, name = '', extras = {}) =>
  sandboxArtifactEditorPath({
    family: 'operational_process_version',
    sandboxId,
    name,
    ...extras,
  });
export const parseDraftReviewRoute = (pathname = '') => {
  const match = String(pathname).match(/^\/app\/(?:build\/review|sandbox)(?:\/([^/]+))?(?:\/([^/]+))?\/?$/);
  return {
    sandboxId: match?.[1] ? decodeURIComponent(match[1]) : '',
    viewId: match?.[2] ? decodeURIComponent(match[2]) : '',
  };
};
/** Only obsolete review URLs redirect. Native editor overlays remain on their page. */
export const legacyBranchReviewDestination = (pathname, search = '') => {
  if (!/^\/app\/sandbox(?:\/|$)/.test(pathname)) return null;
  const params = new URLSearchParams(search);
  const route = parseDraftReviewRoute(pathname);
  return draftReviewPath(route.sandboxId, {
    revisionId: params.get('revision') || params.get('overlay'),
    approvalId: params.get('approval'),
    artifactId: params.get('artifact') || route.viewId,
    family: route.viewId ? 'view' : params.get('family'),
    name: params.get('name'),
  });
};
export const viewBranchPreviewPath = (view, context = {}) => {
  const navigationId = view?.source_artifact_id || view?.__sandbox_source_artifact_id || view?.id;
  const path = navigationId
    ? '/app/views/' + encodeURIComponent(navigationId)
    : view?.name || view?.named
      ? '/app/views/name/' + encodeURIComponent(view.name || view.label)
      : '';
  if (!path || !context.sandboxId) return '';
  return path + '?' + overlayParams(context);
};
export const viewInspectPath = (view, context = {}) => {
  if (context.sandboxId) return viewBranchPreviewPath(view, context);
  if (view?.id) return '/app/views/' + encodeURIComponent(view.id);
  if (view?.named) return '/app/views/name/' + encodeURIComponent(view.name || view.label);
  return '';
};
/** Declared review views (ADR-0045), primary first; empty on older branches. */
export const orderedReviewViews = (reviewViews) =>
  (Array.isArray(reviewViews) ? reviewViews : [])
    .filter((row) => row?.view_id || row?.name)
    .sort((a, b) => Number(Boolean(b.primary)) - Number(Boolean(a.primary)));
export const reviewViewPath = (row, context) =>
  viewBranchPreviewPath({ id: row?.view_id, name: row?.name }, context);
/**
 * Without an explicit selection, the branch's primary review view wins, even
 * for record-only branches. Mixed, deleted and unsupported definitions stay
 * inspectable in Changes.
 */
export const branchOverlayDestination = ({
  sandboxId,
  revisionId,
  approvalId,
  artifacts = [],
  definitions = [],
  previews = {},
  selection = {},
  reviewViews = [],
  canOpenEditors = true,
}) => {
  const context = { sandboxId, revisionId, approvalId };
  const requested = Boolean(selection.artifactId || selection.family || selection.name);
  const primary = orderedReviewViews(reviewViews).find((row) => row.primary);
  const primaryPath = !requested && primary ? reviewViewPath(primary, context) : '';
  if (primaryPath) return primaryPath;
  const matches = artifacts.filter(
    (row) =>
      (!selection.family || row.family === selection.family) &&
      (!selection.name || row.name === selection.name) &&
      (!selection.artifactId ||
        [row.artifact_ref, row.sandbox_artifact_id, row.id].includes(selection.artifactId) ||
        definitions.some(
          (def) =>
            def.family === row.family &&
            def.name === row.name &&
            [def.id, def.sandbox_artifact_id, def.payload?.id].includes(selection.artifactId)
        ))
  );
  const candidates = requested ? matches : artifacts;
  const families = new Set(candidates.map((row) => row.family));
  const item = candidates[0];
  if (!item || families.size !== 1 || item.operation === 'delete')
    return branchChangesPath(context, { canOpenEditors });
  const definition = definitions.find(
    (row) =>
      row.family === item.family &&
      ([row.id, row.sandbox_artifact_id].includes(item.artifact_ref) || row.name === item.name)
  );
  if (item.family === 'view') {
    const view = { id: definition?.payload?.id, source_artifact_id: definition?.source_artifact_id, name: item.name };
    if (view.id || view.name) return viewBranchPreviewPath(view, context);
  }
  if (!canOpenEditors || !isConfigureOverlayFamily(item.family))
    return branchChangesPath(context, { canOpenEditors });
  return sandboxArtifactEditorPath({
    ...context,
    family: item.family,
    name: item.name,
    artifactId: item.artifact_ref || item.sandbox_artifact_id,
  });
};
